/**
 * CommonUI.ts
 *
 * Common logic for UI.
 */

import * as Configuration from "./Configuration/Configuration";
import * as Logging from "./Logging/Logging";
import { processTelemetryEvent } from "./TargetedMessaging/ProcessTelemetryEvent";
import { sendUserFact } from "./TargetedMessaging/SendUserFact";
import * as Theme from "./Theme";
import * as UIStrings from "./UIStrings/UIStrings";
import { loadScript } from "./Window/DomUtils";
import * as Window from "./Window/Window";

import "../../styles/officebrowserfeedback.css";

/**
 * Has the SDK been initialized
 */
let initialized = false;

/**
 * Set ui Strings.
 * @param data the ui strings
 */
function setUIStrings(data: any): void {
	UIStrings.setUIStrings(data);
}

/**
 * Initialize
 */
export function initialize(): Promise<any> {
	return new Promise((resolve, reject) => {
		if (!initialized) {
			const initOptionsCommon = Configuration.get().getCommonInitOptions();
			if (!initOptionsCommon) {
				reject("initOptionsCommon is null");
			}
			
			if((initOptionsCommon.customResourcesSetExternally & Configuration.CustomResources.Css) === Configuration.CustomResources.Css || 	
				initOptionsCommon.stylesUrl) {
				Logging.getLogger().logEvent(Logging.EventIds.Common.Info.VALUE,
					Logging.LogLevel.Info,
					{ Data: `App ${initOptionsCommon.appId} tried to initialize with stylesURL under soft depreciation version` },
					0.01
				);
			}

			Theme.initialize(initOptionsCommon.primaryColour, initOptionsCommon.secondaryColour);

			// Check if custom strings are being passed.
			if ((initOptionsCommon.customResourcesSetExternally & Configuration.CustomResources.Strings) === Configuration.CustomResources.Strings) {
				if (!UIStrings.getUIStrings()) {
					reject("Expected custom strings to be set by host but UIStrings.getUIStrings() is null or undefined");
				}

				initialized = true;
				resolve(null);
			} else {
				const intlFileUrl = initOptionsCommon.intlUrl + initOptionsCommon.locale.toLowerCase() + "/" +
					initOptionsCommon.intlFilename;

				loadScript(intlFileUrl)
					.then(
						() => {
							if (!UIStrings.getUIStrings()) {
								reject("UiStrings were not loaded from " + intlFileUrl);
								return;
							}

							initialized = true;
							resolve(null);
							return;
						}
					).catch(
						(err) => {
							reject("Script load failed for " + intlFileUrl + ". " + err);
						}
					);
			}
		} else {
			resolve(null);
		}
	});
}

/**
 * Reset the module. Used in unit tests.
 */
export function reset() {
	initialized = false;
}

/* Make the the following methods available globally */
Window.setProcessTelemetryEvent(processTelemetryEvent);
Window.setSendUserFact(sendUserFact);
Window.setSetUiStrings(setUIStrings);
